// src/pages/Orders.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PasscodeGate from '../gates/PasswordGate';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  TextField,
  IconButton,
  Button,
  Select,
  MenuItem,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  Chip,
  Switch,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import { FaCcVisa, FaCcMastercard, FaCcPaypal } from 'react-icons/fa';
import { SiApplepay, SiGooglepay } from 'react-icons/si'; // optional, safe to remove if not wanted

// Currency formatter
const fmtCurrency = value => (typeof value === 'number' ? `€${value.toFixed(2)}` : '—');
// Icons
const brandIcons = {
  visa: <FaCcVisa size={24} />,
  mastercard: <FaCcMastercard size={24} />,
  paypal: <FaCcPaypal size={24} />,
};
const methodIcons = {
  paypal: <FaCcPaypal size={24} />,
  apple_pay: <SiApplepay size={24} />,
  google_pay: <SiGooglepay size={24} />,
};

/** Normalize Stripe PI → a compact payment summary for UI */
function summarizePayment(order, pi) {
  const latestCharge = pi?.latest_charge || pi?.charges?.data?.[0];
  const pmd = latestCharge?.payment_method_details || {};
  const pm  = pi?.payment_method;

  // The ACTUAL method used (prefer charge.payment_method_details.type)
  const type =
    pmd?.type ??
    pm?.type ??
    order?.paymentMethod?.type ??
    null;

  // ── PayPal ───────────────────────────────────────────────
  if (type === 'paypal' || pmd?.paypal) {
    const payerEmail =
      pmd?.paypal?.payer_email ||
      latestCharge?.billing_details?.email ||
      pi?.receipt_email ||
      order?.paymentMethod?.payerEmail ||
      order?.email;
    return {
      kind: 'paypal',
      title: `PayPal${payerEmail ? ` (${payerEmail})` : ''}`,
      subtitle: '',
      icon: methodIcons.paypal,
    };
  }

  // ── Card (includes Apple Pay / Google Pay wallets) ───────
  if (type === 'card' || pmd?.card || pm?.card || order?.paymentMethod?.type === 'card') {
    const card = pmd?.card || pm?.card || order?.paymentMethod || {};
    const brand = (card.brand || '').toLowerCase();
    const last4 = card.last4 || order?.paymentMethod?.last4;
    const expMonth = card.exp_month || order?.paymentMethod?.expMonth;
    const expYear  = card.exp_year  || order?.paymentMethod?.expYear;

    // wallet.type is 'apple_pay' | 'google_pay' when a wallet was used
    const walletType = (card?.wallet?.type || pmd?.card?.wallet?.type || '').toLowerCase();
    const via = walletType === 'apple_pay' ? 'Apple Pay'
              : walletType === 'google_pay' ? 'Google Pay'
              : 'Card';

    return {
      kind: walletType || 'card',
      title: `${via}${last4 ? ` ••••${last4}` : ''}`,
      subtitle: brand
        ? `${brand.toUpperCase()}${expMonth ? ` • Expires ${String(expMonth).padStart(2,'0')}/${expYear || ''}` : ''}`
        : '',
      icon: methodIcons[walletType] || brandIcons[brand] || brandIcons.visa,
    };
  }

  // ── Other redirect/bank methods (iDEAL, Sofort, Bancontact, Giropay, EPS, P24, SEPA, etc.) ──
  const generic = type || 'unknown';
  const detail  = pmd[generic] || {};
  const bank    = detail.bank || detail.bank_name || detail.bank_code || '';
  return {
    kind: generic,
    title: generic.toUpperCase() + (bank ? ` • ${bank}` : ''),
    subtitle: '',
    icon: methodIcons[generic] || null,
  };
}


/* ───── row component (as before) ─────────────────────────────────────────── */
function OrderRow({ order, onUpdate, isOpen, toggleOpen }) {
  const [tracking, setTracking] = useState(order.trackingNumber || '');
  const [status, setStatus] = useState(order.status || 'ordered');
  const [pi, setPi] = useState(null);
  const [loadingPm, setLoadingPm] = useState(false);

  const placedAt = order.createdAt
    ? format(new Date(order.createdAt), 'yyyy-MM-dd HH:mm')
    : '—';
  const isDigital = !order.shippingAddress;
  const summary = useMemo(() => summarizePayment(order, pi), [order, pi]);

  useEffect(() => {
    if (!isOpen || pi) return;
    setLoadingPm(true);
    fetch(`/stripe/payment-intent/${order.id}`)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(setPi)
      .catch(err => console.error('payment-method fetch:', err))
      .finally(() => setLoadingPm(false));
  }, [isOpen, order.id, pi]);

  const toggleFulfilled = e => onUpdate(order.id, { fulfilled: e.target.checked });
  const saveTracking = () => onUpdate(order.id, { trackingNumber: tracking });
  const saveStatus = () => onUpdate(order.id, { status });

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => toggleOpen(order.id)}>
            {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{order.id}</TableCell>
        <TableCell>{placedAt}</TableCell>
        <TableCell>{order.customerName || order.email || '—'}</TableCell>
        <TableCell>
          <Switch
            checked={!!order.fulfilled}
            onChange={toggleFulfilled}
            size="small"
            color="success"
          />
          <Chip
            label={order.fulfilled ? 'Fulfilled' : 'Pending'}
            size="small"
            color={order.fulfilled ? 'success' : 'default'}
          />
        </TableCell>
        <TableCell align="right">{fmtCurrency(order.total)}</TableCell>
        <TableCell align="right">
          {isDigital ? '—' : fmtCurrency(order.shippingCost)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, borderBottom: 'none' }}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2 }}>
              <Typography variant="subtitle1">Items</Typography>
              {order.items.map((it, i) => (
                <Typography key={i} variant="body2">
                  • {it.name} ×{it.qty} — €{(it.subtotal || 0).toFixed(2)}
                </Typography>
              ))}

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Shipping Address</Typography>
                {isDigital ? (
                  <Typography>— (digital order – no shipping)</Typography>
                ) : (
                  <Typography>
                    {order.shippingAddress.firstName}{' '}
                    {order.shippingAddress.lastName}, {order.shippingAddress.address1},{' '}
                    {order.shippingAddress.city}, {order.shippingAddress.postalCode},{' '}
                    {order.shippingAddress.country}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Payment Method</Typography>
                {loadingPm && <CircularProgress size={20} />}
                {!loadingPm && summary && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {summary.icon || null}
                    <Box>
                      <Typography variant="body2">{summary.title}</Typography>
                      {summary.subtitle ? (
                        <Typography variant="caption">{summary.subtitle}</Typography>
                      ) : null}
                    </Box>
                  </Box>
                )}
                {!loadingPm && !summary && <Typography variant="body2">—</Typography>}
              </Box>

              {!isDigital && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <TextField
                        label="Tracking Number"
                        value={tracking}
                        onChange={e => setTracking(e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item>
                      <Button size="small" onClick={saveTracking}>
                        Save
                      </Button>
                    </Grid>
                    <Grid item>
                      <Select
                        value={status}
                        onChange={e => setStatus(e.target.value)}
                        size="small"
                      >
                        <MenuItem value="ordered">Ordered</MenuItem>
                        <MenuItem value="confirmed">Confirmed</MenuItem>
                        <MenuItem value="dispatched">Dispatched</MenuItem>
                        <MenuItem value="delivered">Delivered</MenuItem>
                      </Select>
                    </Grid>
                    <Grid item>
                      <Button size="small" onClick={saveStatus}>
                        Save Status
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function OrdersInner() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [openRows, setOpenRows] = useState(new Set());

  const fetchOrders = useCallback(() => {
    setLoading(true);
    fetch('/orders')
      .then(res => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(setOrders)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateOrder = (id, updates) => {
    setOrders(prev => prev.map(o => (o.id === id ? { ...o, ...updates } : o)));
    fetch(`/orders/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
      .then(res => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(updated => setOrders(prev => prev.map(o => (o.id === id ? updated : o))))
      .catch(console.error);
  };

  // Filtering
  const filtered = useMemo(() =>
    orders.filter(o => {
      if (statusFilter !== 'all' && o.status !== statusFilter) return false;
      if (dateRange.from && parseISO(o.createdAt) < parseISO(dateRange.from)) return false;
      if (dateRange.to && parseISO(o.createdAt) > parseISO(dateRange.to)) return false;
      if (!searchText) return true;
      const term = searchText.toLowerCase();
      return (
        o.id.toString().includes(term) ||
        (o.customerName || o.email || '').toLowerCase().includes(term)
      );
    }),
  [orders, searchText, statusFilter, dateRange]);

  // Metrics
  const totalSales = useMemo(() => filtered.reduce((sum, o) => sum + (o.total || 0), 0), [filtered]);
  const totalOrders = filtered.length;
  const avgOrder = totalOrders ? totalSales / totalOrders : 0;

  // Chart data
  const chartData = useMemo(() => {
    const daily = {};
    filtered.forEach(o => {
      const day = format(new Date(o.createdAt), 'yyyy-MM-dd');
      daily[day] = (daily[day] || 0) + (o.total || 0);
    });
    return Object.entries(daily)
      .sort(([a], [b]) => new Date(a) - new Date(b))
      .map(([date, value]) => ({ date, value }));
  }, [filtered]);

  const toggleOpen = id => {
    setOpenRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Orders Dashboard</Typography>

      {/* Metrics + Chart */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 3, mb: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="textSecondary">Total Sales</Typography>
            <Typography variant="h6">{fmtCurrency(totalSales)}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="textSecondary">Total Orders</Typography>
            <Typography variant="h6">{totalOrders}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="textSecondary">Avg. Order Value</Typography>
            <Typography variant="h6">{fmtCurrency(avgOrder)}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="subtitle2">Sales Over Time</Typography>
            <Box sx={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <ReTooltip formatter={value => fmtCurrency(value)} />
                  <Line type="monotone" dataKey="value" stroke="#1976d2" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filter panel */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr repeat(auto-fit, minmax(150px, auto)) auto', gap: 2, mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f5f5f5', px: 2, borderRadius: 1 }}>
          <SearchIcon color="action" />
          <TextField
            placeholder="Search orders or customers"
            variant="standard"
            size="small"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            sx={{ ml: 1, width: '100%' }}
          />
        </Box>
        <Select size="small" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="ordered">Ordered</MenuItem>
          <MenuItem value="confirmed">Confirmed</MenuItem>
          <MenuItem value="dispatched">Dispatched</MenuItem>
          <MenuItem value="delivered">Delivered</MenuItem>
        </Select>
        <TextField
          label="From"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dateRange.from}
          onChange={e => setDateRange(r => ({ ...r, from: e.target.value }))}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dateRange.to}
          onChange={e => setDateRange(r => ({ ...r, to: e.target.value }))}
        />
        <Button variant="contained" onClick={fetchOrders} sx={{ justifySelf: 'end' }}>Refresh</Button>
      </Box>

      {/* Orders Table */}
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Order #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Total (€)</TableCell>
              <TableCell align="right">Shipping (€)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(o => (
              <OrderRow
                key={o.id}
                order={o}
                onUpdate={updateOrder}
                isOpen={openRows.has(o.id)}
                toggleOpen={toggleOpen}
              />
            ))}
          </TableBody>
        </Table>
      </Paper>

      {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
    </Container>
  );
}
export default function Orders() {
  return (
    <PasscodeGate areaKey="orders">
      <OrdersInner />
    </PasscodeGate>
  );
}
